import {message} from "antd";
import _ from "lodash";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {SbomComponentServiceContext} from "../../Contexts";
import SbomComponent from "../../domain/SbomComponent";
import SbomComponentFinding from "../../domain/SbomComponentFinding";
import QueryOptions from "../../sal-ui/QueryOptions";
import {SbomComponentFindingSort, SbomComponentOption} from "../../service/SbomComponentService";
import {ComponentFindingsPage} from "./FocusedFindingSelect";

export interface FocusedFindingModalState {
    component: SbomComponent;
    findingId?: string;
}

export interface FocusedComponentOptionsPage {
    total: number;
    data: SbomComponentOption[];
    skip: number;
    search: string;
}

export interface FocusedNavigationComponentsPage {
    total: number;
    data: SbomComponent[];
    skip: number;
}

type ComponentFindingsByComponentId = Record<string, ComponentFindingsPage | undefined>;

interface FocusedFindingAnalysisOptions {
    projectVersionId?: string;
    componentFindingsPageSize: number;
    findingOptionPageSize: number;
    navigationComponentsPageSize: number;
    componentOptionLoadMoreThresholdPx: number;
    navigationComponentsOrderBy: string;
    vulnerableComponentsRqbQuery: any;
}

export interface FocusedFindingAnalysisController {
    state?: FocusedFindingModalState;
    componentsFindings: ComponentFindingsByComponentId;
    navigationComponentsPage?: FocusedNavigationComponentsPage;
    navigationComponents: SbomComponent[];
    componentFindingsPageSize: number;
    findingOptionPageSize: number;
    componentOptionsPage?: FocusedComponentOptionsPage;
    componentOptionsLoading: boolean;
    componentSelectOpen: boolean;
    close: () => void;
    open: (component: SbomComponent, finding: SbomComponentFinding) => void;
    focusComponent: (component: SbomComponent, reloadNavigationIfMissing?: boolean) => void;
    moveComponent: (offset: number) => void;
    moveFinding: (offset: number) => void;
    canMoveComponentPrev: (componentIndex: number) => boolean;
    canMoveComponentNext: (componentIndex: number, components: SbomComponent[]) => boolean;
    reloadComponentFindings: (componentId: string, skip?: number, sort?: SbomComponentFindingSort) => Promise<ComponentFindingsPage | undefined>;
    reloadNavigationIfModalOpen: () => void;
    reloadExpandedComponentFindings: () => void;
    selectFinding: (findingId: string) => void;
    setComponentSelectOpen: (open: boolean) => void;
    searchComponentOptions: (search: string) => void;
    onComponentOptionsPopupScroll: (event: React.UIEvent<HTMLDivElement>) => void;
    selectComponent: (componentId: string) => void;
}

export interface FocusedFindingAnalysisUrlSyncController {
    state?: FocusedFindingModalState;
    reloadComponentFindings: (componentId: string, skip?: number, sort?: SbomComponentFindingSort) => Promise<ComponentFindingsPage | undefined>;
    reloadFocusedFindingPage: (componentId: string, findingId: string) => Promise<ComponentFindingsPage | undefined>;
    reloadNavigationComponentsForComponent: (componentId: string) => Promise<FocusedNavigationComponentsPage | undefined>;
    updateModal: (state: React.SetStateAction<FocusedFindingModalState | undefined>) => void;
    clearNavigationComponents: () => void;
    beginRestore: () => number;
    finishRestore: () => void;
    cancelRestore: () => void;
    isRestoreCurrent: (requestId: number) => boolean;
    isRestorePending: () => boolean;
}

export interface FocusedFindingAnalysis {
    controller: FocusedFindingAnalysisController;
    urlSync: FocusedFindingAnalysisUrlSyncController;
}

export function useFocusedFindingAnalysis(options: FocusedFindingAnalysisOptions): FocusedFindingAnalysis {
    const sbomComponentService = useContext(SbomComponentServiceContext);
    const projectVersionIdRef = useRef(options.projectVersionId);
    const [navigationComponentsPage, setNavigationComponentsPage] = useState<FocusedNavigationComponentsPage>();
    const navigationComponentsPageRef = useRef<FocusedNavigationComponentsPage>();
    const [componentOptionsPage, setComponentOptionsPage] = useState<FocusedComponentOptionsPage>();
    const componentOptionsPageRef = useRef<FocusedComponentOptionsPage>();
    const [componentOptionsLoading, setComponentOptionsLoading] = useState(false);
    const componentOptionsRequestIdRef = useRef(0);
    const [componentSelectOpen, setComponentSelectOpen] = useState(false);
    const componentSelectOpenRef = useRef(false);
    const [componentsFindings, setComponentsFindings] = useState<ComponentFindingsByComponentId>({});
    const componentsFindingsRef = useRef<ComponentFindingsByComponentId>({});
    const [state, setState] = useState<FocusedFindingModalState>();
    const stateRef = useRef<FocusedFindingModalState>();
    const navigationComponentsRequestIdRef = useRef(0);
    const restorePendingRef = useRef(false);
    const restoreRequestIdRef = useRef(0);

    const reloadComponentOptions = useCallback((search: string, skip: number, append: boolean) => {
        const projectVersionId = projectVersionIdRef.current;

        if (projectVersionId === undefined) {
            return;
        }

        const requestId = ++componentOptionsRequestIdRef.current;

        setComponentOptionsLoading(true);

        sbomComponentService.getComponentOptions(projectVersionId, options.navigationComponentsPageSize, skip, search)
            .then(componentOptions => {
                if (componentOptionsRequestIdRef.current !== requestId) {
                    return;
                }

                setComponentOptionsPage(prevPage => {
                    const appendToPreviousPage = append && prevPage?.search === search;
                    const data = appendToPreviousPage ? [...prevPage.data, ...componentOptions.data] : componentOptions.data;

                    const nextPage = {
                        total: componentOptions.total,
                        data,
                        skip: appendToPreviousPage ? prevPage.skip : skip,
                        search
                    };

                    componentOptionsPageRef.current = nextPage;

                    return nextPage;
                });
            })
            .catch(() => {
                if (componentOptionsRequestIdRef.current !== requestId) {
                    return;
                }

                message.error("Cannot retrieve vulnerability analysis component options.");
            })
            .finally(() => {
                if (componentOptionsRequestIdRef.current === requestId) {
                    setComponentOptionsLoading(false);
                }
            });
    }, [options.navigationComponentsPageSize, sbomComponentService]);

    const debouncedReloadComponentOptions = useMemo(
        () => _.debounce(reloadComponentOptions, 300),
        [reloadComponentOptions]
    );

    useEffect(() => {
        projectVersionIdRef.current = options.projectVersionId;
        clearComponentData();
    }, [options.projectVersionId]);

    useEffect(() => {
        componentsFindingsRef.current = componentsFindings;
    }, [componentsFindings]);

    useEffect(() => () => debouncedReloadComponentOptions.cancel(), [debouncedReloadComponentOptions]);

    useEffect(() => {
        if (!componentSelectOpen) {
            return;
        }

        if (shouldLoadDefaultComponentOptions()) {
            reloadComponentOptions("", 0, false);
        }
    }, [componentSelectOpen, reloadComponentOptions]);

    useEffect(() => {
        resetComponentOptions();

        if (componentSelectOpenRef.current) {
            reloadComponentOptions("", 0, false);
        }
    }, [state?.component?.id, reloadComponentOptions]);

    useEffect(() => {
        const componentId = state?.component?.id;

        if (componentId === undefined) {
            return;
        }

        if (restorePendingRef.current) {
            return;
        }

        const findingsPage = componentsFindings[componentId];

        if (findingsPage === undefined) {
            return;
        }

        if (findingsPage.data.length === 0) {
            if (findingsPage.total > 0 && findingsPage.skip > 0) {
                reloadComponentFindings(componentId);

                return;
            }

            updateModal(prevState => prevState?.component?.id === componentId && prevState.findingId !== undefined
                ? {...prevState, findingId: undefined}
                : prevState);

            return;
        }

        const findings = findingsPage.data;
        const currentFindingId = state.findingId;

        if (currentFindingId !== undefined && findings.some(finding => finding.id === currentFindingId)) {
            return;
        }

        const nextFindingId = findings[0].id;

        updateModal(prevState => prevState?.component?.id === componentId && prevState.findingId !== nextFindingId
            ? {...prevState, findingId: nextFindingId}
            : prevState);
    }, [state?.component?.id, state?.findingId, componentsFindings]);

    const controller: FocusedFindingAnalysisController = {
        state,
        componentsFindings,
        navigationComponentsPage,
        navigationComponents: getNavigationComponents(),
        componentFindingsPageSize: options.componentFindingsPageSize,
        findingOptionPageSize: options.findingOptionPageSize,
        componentOptionsPage,
        componentOptionsLoading,
        componentSelectOpen,
        close,
        open,
        focusComponent,
        moveComponent,
        moveFinding,
        canMoveComponentPrev,
        canMoveComponentNext,
        reloadComponentFindings,
        reloadNavigationIfModalOpen,
        reloadExpandedComponentFindings,
        selectFinding,
        setComponentSelectOpen: setComponentSelectOpenState,
        searchComponentOptions,
        onComponentOptionsPopupScroll,
        selectComponent
    };

    return {
        controller,
        urlSync: {
            state,
            reloadComponentFindings,
            reloadFocusedFindingPage,
            reloadNavigationComponentsForComponent,
            updateModal,
            clearNavigationComponents,
            beginRestore,
            finishRestore,
            cancelRestore,
            isRestoreCurrent,
            isRestorePending
        }
    };

    function reloadComponentFindings(componentId: string, skip: number = 0, sort: SbomComponentFindingSort = SbomComponentFindingSort.MOST_IMPORTANT) {
        const queryOptions = new QueryOptions("", options.componentFindingsPageSize, skip);

        return sbomComponentService.getFindings(componentId, queryOptions, sort)
            .then(findings => {
                const findingsPage = {
                    total: findings.total,
                    data: findings.data,
                    skip,
                    top: options.componentFindingsPageSize,
                    sort
                };

                setComponentsFindings(prevState => {
                    return {
                        ...prevState,
                        [componentId]: findingsPage
                    };
                });

                return findingsPage;
            })
            .catch(() => {
                message.error("Cannot retrieve SBOM component findings.");

                return undefined;
            });
    }

    function reloadFocusedFindingPage(componentId: string, findingId: string) {
        const queryOptions = new QueryOptions("", options.findingOptionPageSize, 0);

        return sbomComponentService.getFindingOptions(componentId, queryOptions, findingId, {sort: SbomComponentFindingSort.MOST_IMPORTANT})
            .then(findingOptions => {
                const option = findingOptions.data.find(value => value.id === findingId);

                if (option === undefined) {
                    return undefined;
                }

                const skip = Math.floor(option.position / options.componentFindingsPageSize) * options.componentFindingsPageSize;

                return reloadComponentFindings(componentId, skip);
            })
            .catch(() => undefined);
    }

    function close() {
        cancelRestore();

        updateModal(undefined);
        clearNavigationComponents();
    }

    function updateNavigationComponentsPage(page: FocusedNavigationComponentsPage | undefined) {
        navigationComponentsPageRef.current = page;
        setNavigationComponentsPage(page);
    }

    function setComponentSelectOpenState(open: boolean) {
        componentSelectOpenRef.current = open;
        setComponentSelectOpen(open);
    }

    function clearNavigationComponents() {
        navigationComponentsRequestIdRef.current += 1;

        updateNavigationComponentsPage(undefined);
        setComponentSelectOpenState(false);
        resetComponentOptions();
    }

    function resetComponentOptions() {
        debouncedReloadComponentOptions.cancel();
        componentOptionsRequestIdRef.current += 1;

        componentOptionsPageRef.current = undefined;
        setComponentOptionsPage(undefined);
        setComponentOptionsLoading(false);
    }

    function shouldLoadDefaultComponentOptions() {
        const optionsPage = componentOptionsPageRef.current;

        return optionsPage === undefined || optionsPage.search !== "";
    }

    function clearComponentData() {
        updateModal(undefined);
        clearNavigationComponents();
        setComponentsFindings({});
        componentsFindingsRef.current = {};
    }

    function updateModal(nextState: React.SetStateAction<FocusedFindingModalState | undefined>) {
        if (typeof nextState === "function") {
            setState(prevState => {
                const resolvedState = nextState(prevState);

                stateRef.current = resolvedState;

                return resolvedState;
            });

            return;
        }

        stateRef.current = nextState;
        setState(nextState);
    }

    function beginRestore() {
        restoreRequestIdRef.current += 1;
        restorePendingRef.current = true;

        return restoreRequestIdRef.current;
    }

    function finishRestore() {
        restorePendingRef.current = false;
    }

    function cancelRestore() {
        restoreRequestIdRef.current += 1;
        restorePendingRef.current = false;
    }

    function isRestoreCurrent(requestId: number) {
        return restoreRequestIdRef.current === requestId;
    }

    function isRestorePending() {
        return restorePendingRef.current;
    }

    function nextNavigationComponentsRequestId() {
        navigationComponentsRequestIdRef.current += 1;

        return navigationComponentsRequestIdRef.current;
    }

    function isNavigationComponentsRequestCurrent(requestId: number) {
        return navigationComponentsRequestIdRef.current === requestId;
    }

    function open(component: SbomComponent, finding: SbomComponentFinding) {
        cancelRestore();

        updateModal({component, findingId: finding.id});
        reloadNavigationComponentsForComponent(component.id);

        if (componentsFindings[component.id] === undefined) {
            reloadComponentFindings(component.id);
        }
    }

    function moveFinding(offset: number) {
        const componentId = state?.component?.id;

        if (componentId === undefined) {
            return;
        }

        const findingsPage = componentsFindings[componentId];
        const findings = findingsPage?.data;
        const currentIndex = findings?.findIndex(value => value.id === state.findingId) ?? -1;
        const nextIndex = currentIndex + offset;
        const nextFinding = findings?.[nextIndex];

        if (nextFinding === undefined) {
            moveFindingToAdjacentPage(componentId, findingsPage, nextIndex);

            return;
        }

        updateModal(prevState => prevState ? {...prevState, findingId: nextFinding.id} : prevState);
    }

    function moveFindingToAdjacentPage(componentId: string, findingsPage: ComponentFindingsPage | undefined, nextIndex: number) {
        if (findingsPage === undefined) {
            return;
        }

        if (nextIndex < 0 && findingsPage.skip > 0) {
            const nextSkip = Math.max(0, findingsPage.skip - findingsPage.top);

            reloadComponentFindings(componentId, nextSkip, findingsPage.sort)
                .then(nextPage => setFindingToPageEdge(nextPage, "last"));

            return;
        }

        if (nextIndex >= findingsPage.data.length && findingsPage.skip + findingsPage.data.length < findingsPage.total) {
            reloadComponentFindings(componentId, findingsPage.skip + findingsPage.top, findingsPage.sort)
                .then(nextPage => setFindingToPageEdge(nextPage, "first"));
        }
    }

    function setFindingToPageEdge(findingsPage: ComponentFindingsPage | undefined, edge: "first" | "last") {
        if (findingsPage === undefined || findingsPage.data.length === 0) {
            return;
        }

        const finding = edge === "first" ? findingsPage.data[0] : findingsPage.data[findingsPage.data.length - 1];

        updateModal(prevState => prevState ? {...prevState, findingId: finding.id} : prevState);
    }

    function moveComponent(offset: number) {
        const component = state?.component;

        if (component === undefined) {
            return;
        }

        const components = getNavigationComponents();
        const currentIndex = components.findIndex(value => value.id === component.id);

        if (currentIndex < 0) {
            reloadNavigationComponentsForComponent(component.id)
                .then(page => {
                    if (page !== undefined) {
                        moveComponentInPage(component.id, offset, page);
                    }
                });

            return;
        }

        moveComponentInList(offset, currentIndex, components);
    }

    function moveComponentInPage(componentId: string, offset: number, page: FocusedNavigationComponentsPage) {
        const currentIndex = page.data.findIndex(value => value.id === componentId);

        if (currentIndex < 0) {
            return;
        }

        moveComponentInList(offset, currentIndex, page.data, page);
    }

    function moveComponentInList(offset: number,
                                 currentIndex: number,
                                 components: SbomComponent[],
                                 page: FocusedNavigationComponentsPage | undefined = navigationComponentsPage) {
        const nextComponent = components[currentIndex + offset];

        if (nextComponent === undefined) {
            moveComponentToAdjacentPage(offset, currentIndex, components.length, page);

            return;
        }

        focusComponent(nextComponent);
    }

    function moveComponentToAdjacentPage(offset: number,
                                         currentIndex: number,
                                         navigationComponentsCount: number,
                                         page: FocusedNavigationComponentsPage | undefined) {
        if (page === undefined) {
            return;
        }

        if (offset < 0 && currentIndex <= 0 && page.skip > 0) {
            const prevSkip = Math.max(0, page.skip - options.navigationComponentsPageSize);

            reloadNavigationComponents(prevSkip)
                .then(prevPage => {
                    if (prevPage === undefined) {
                        return;
                    }

                    focusComponentAtPageEdge(prevPage, "last");
                });

            return;
        }

        if (offset > 0 && currentIndex >= navigationComponentsCount - 1 && page.skip + page.data.length < page.total) {
            const nextSkip = page.skip + options.navigationComponentsPageSize;

            reloadNavigationComponents(nextSkip)
                .then(nextPage => focusComponentAtPageEdge(nextPage, "first"));
        }
    }

    function focusComponentAtPageEdge(page: FocusedNavigationComponentsPage | undefined, edge: "first" | "last") {
        if (page === undefined) {
            return;
        }

        if (page.data.length > 0) {
            const component = edge === "first" ? page.data[0] : page.data[page.data.length - 1];

            focusComponent(component, false);
        }
    }

    function searchComponentOptions(search: string) {
        debouncedReloadComponentOptions(search, 0, false);
    }

    function onComponentOptionsPopupScroll(event: React.UIEvent<HTMLDivElement>) {
        if (componentOptionsPage === undefined || componentOptionsLoading) {
            return;
        }

        if (componentOptionsPage.data.length >= componentOptionsPage.total) {
            return;
        }

        const target = event.currentTarget;
        const bottomDistance = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (bottomDistance <= options.componentOptionLoadMoreThresholdPx) {
            reloadComponentOptions(
                componentOptionsPage.search,
                componentOptionsPage.skip + componentOptionsPage.data.length,
                true
            );
        }
    }

    function selectComponent(componentId: string) {
        const selectedOption = componentOptionsPage?.data.find(option => option.component.id === componentId);

        if (selectedOption !== undefined) {
            reloadNavigationComponentsPageForOption(selectedOption)
                .then(page => {
                    if (page?.data.some(component => component.id === selectedOption.component.id)) {
                        focusComponent(selectedOption.component, false);
                    }
                });

            return;
        }

        const component = getNavigationComponents().find(value => value.id === componentId);

        if (component === undefined) {
            return;
        }

        focusComponent(component);
    }

    function reloadNavigationComponentsPageForOption(option: SbomComponentOption) {
        const skip = Math.floor(option.position / options.navigationComponentsPageSize) * options.navigationComponentsPageSize;

        return reloadNavigationComponents(skip);
    }

    function focusComponent(component: SbomComponent, reloadNavigationIfMissing = true) {
        cancelRestore();

        const findings = componentsFindings[component.id]?.data;

        updateModal({component, findingId: findings?.[0]?.id});

        if (reloadNavigationIfMissing && navigationComponentsPageRef.current === undefined) {
            reloadNavigationComponentsForComponent(component.id);
        }

        if (findings === undefined) {
            reloadComponentFindings(component.id)
                .then(findingsPage => {
                    const findingId = findingsPage?.data[0]?.id;

                    if (findingId !== undefined) {
                        updateModal(prevState => prevState?.component.id === component.id
                            ? {...prevState, findingId}
                            : prevState);
                    }
                });
        }
    }

    function selectFinding(findingId: string) {
        cancelRestore();

        updateModal(prevState => prevState ? {...prevState, findingId} : prevState);
    }

    function hasVulnerabilityFindings(component: SbomComponent) {
        return Object.keys(component.findingStatistics ?? {}).length > 0;
    }

    function getNavigationComponents() {
        if (navigationComponentsPage !== undefined) {
            return navigationComponentsPage.data;
        }

        const component = state?.component;

        if (component === undefined || !hasVulnerabilityFindings(component)) {
            return [];
        }

        return [component];
    }

    function canMoveComponentPrev(componentIndex: number) {
        if (componentIndex < 0) {
            return false;
        }

        if (componentIndex > 0) {
            return true;
        }

        const page = navigationComponentsPage;

        return page !== undefined && page.skip > 0;
    }

    function canMoveComponentNext(componentIndex: number, components: SbomComponent[]) {
        if (componentIndex < 0) {
            return false;
        }

        if (componentIndex < components.length - 1) {
            return true;
        }

        const page = navigationComponentsPage;

        return page !== undefined && page.skip + page.data.length < page.total;
    }

    function reloadNavigationComponentsForComponent(componentId: string) {
        const requestId = nextNavigationComponentsRequestId();

        return reloadNavigationComponentsPageForComponent(componentId, requestId);
    }

    function reloadNavigationComponentsPageForComponent(componentId: string, requestId: number) {
        const projectVersionId = projectVersionIdRef.current;

        if (projectVersionId === undefined) {
            return Promise.resolve(undefined);
        }

        return sbomComponentService.getComponentOptions(projectVersionId, options.navigationComponentsPageSize, 0, "", componentId)
            .then(componentOptions => {
                if (!isNavigationComponentsRequestCurrent(requestId)) {
                    return undefined;
                }

                const option = componentOptions.data.find(value => value.component.id === componentId);

                if (option === undefined) {
                    message.error("Cannot locate component in navigation list.");

                    return undefined;
                }

                const skip = Math.floor(option.position / options.navigationComponentsPageSize) * options.navigationComponentsPageSize;

                return loadNavigationComponents(skip, requestId);
            })
            .catch(() => {
                if (!isNavigationComponentsRequestCurrent(requestId)) {
                    return undefined;
                }

                message.error("Cannot retrieve vulnerability analysis component options.");

                return undefined;
            });
    }

    function reloadNavigationIfModalOpen() {
        if (stateRef.current === undefined) {
            return;
        }

        const currentState = stateRef.current;
        const page = navigationComponentsPageRef.current;
        const skip = page?.skip ?? 0;

        loadNavigationComponents(skip, nextNavigationComponentsRequestId()).then(reloadedPage => {
            if (reloadedPage?.data.some(component => component.id === currentState.component.id)) {
                return;
            }

            reloadNavigationComponentsForComponent(currentState.component.id);
        });

        resetComponentOptions();

        if (componentSelectOpenRef.current) {
            reloadComponentOptions("", 0, false);
        }
    }

    function reloadNavigationComponents(skip: number = 0) {
        const requestId = nextNavigationComponentsRequestId();

        return loadNavigationComponents(skip, requestId);
    }

    function loadNavigationComponents(skip: number, requestId: number) {
        const projectVersionId = projectVersionIdRef.current;

        if (projectVersionId === undefined) {
            return Promise.resolve(undefined);
        }

        const queryOptions = createNavigationQueryOptions(skip);

        return sbomComponentService.getList(projectVersionId, queryOptions)
            .then(components => {
                if (!isNavigationComponentsRequestCurrent(requestId)) {
                    return undefined;
                }

                const page = {
                    total: components.total,
                    data: components.data,
                    skip
                };

                updateNavigationComponentsPage(page);

                return page;
            })
            .catch(() => {
                if (!isNavigationComponentsRequestCurrent(requestId)) {
                    return undefined;
                }

                message.error("Cannot retrieve vulnerability analysis components.");

                return undefined;
            });
    }

    function createNavigationQueryOptions(skip: number) {
        return new QueryOptions(
            options.navigationComponentsOrderBy,
            options.navigationComponentsPageSize,
            skip,
            undefined,
            undefined,
            options.vulnerableComponentsRqbQuery
        );
    }

    function reloadExpandedComponentFindings() {
        Object.entries(componentsFindingsRef.current)
            .forEach(([componentId, findingsPage]) => reloadComponentFindings(componentId, findingsPage?.skip ?? 0, findingsPage?.sort));
    }
}
